<template>
  <Flex direction="column" align="center" :gap="2">
    <span class="label">
      <template v-if="isLoading">
        {{ t('sign_in.verifying') }}
      </template>
      <template v-else>
        {{ t('sign_in.input_label', { phoneNumber: props.usersPhone }) }}
      </template>
    </span>
    <div ref="otpCont" :class="`input-wrapper ${!initialized ? 'hidden' : ''}`">
      <TextField
        v-for="(num, i) in otpValue.length"
        :key="num + i"
        v-model="otpValue[i]"
        :autofocus="i === 0"
        :name="`otp${i}`"
        autocomplete="one-time-code"
        inputmode="numeric"
        :max-length="1"
        type="tel"
        :disabled="isLoading"
        @keydown="handleKeyDown($event, i)"
        @paste="handlePaste($event)" />
    </div>
  </Flex>
</template>

<script lang="ts" setup>
import TextField from '@/components/TextField/TextField.vue';
import Flex from '@/components/Flex/Flex.vue';
import { useI18n } from 'vue-i18n';
import { computed, onMounted, ref, watch } from 'vue';
import { useSubmitOtpCode } from '@/features/SmsCodeVerification/components/OTPInputForm/composables/useSubmitOtpCode';
import { useToast } from '@/composables/useToast';
import { useRouter } from 'vue-router';
import { useAuth } from '@/composables/useAuth';

const props = defineProps<{
  usersPhone: string;
  otpSent: boolean;
}>();

const { t } = useI18n();
const { showToast } = useToast();
const router = useRouter();
const auth = useAuth();

const { mutateAsync: submitOtpMutation, isPending: OTPSubmitLoading } =
  useSubmitOtpCode(props.usersPhone);

const isSubmitting = ref(false);
const isLoading = computed(() => OTPSubmitLoading.value || isSubmitting.value);

const initialized = ref(false);
const otpValue = ref(['', '', '', '', '', '']);
const otpCont = ref<HTMLDivElement | null>(null);

onMounted(() => {
  // to hide count label from inputs and center input value
  setTimeout(() => {
    const allInputs = otpCont.value?.querySelectorAll('md-filled-text-field');

    allInputs?.forEach((element, index) => {
      setTimeout(() => {
        if (index === 0) {
          element.focus();
        }
      }, 100);

      const filledField =
        element.shadowRoot?.querySelector('md-filled-field')?.shadowRoot;

      const supportingText = filledField?.querySelector(
        '.supporting-text'
      ) as HTMLDivElement;

      supportingText.style.visibility = 'hidden';

      const inputWrapper = element?.shadowRoot?.querySelector(
        '.input-wrapper'
      ) as HTMLDivElement;

      inputWrapper.style.textAlign = 'center';

      // to remove autofill color from phones/tablets
      // CSS vars doesn't work here due to shadow dom
      const style = document.createElement('style');
      style.textContent = `
        @media only screen and (max-width : 768px) {
          .input-wrapper {
            background-color: #dee3e6 !important;
            }
        }

        input:-webkit-autofill,
        input:-webkit-autofill:hover,
        input:-webkit-autofill:active,
        input:-webkit-autofill:focus {
          background-color: #dee3e6 !important;
          color: #40484b !important;
          -webkit-box-shadow: 0 0 0 1000px #dee3e6 inset !important;
          -webkit-text-fill-color: #40484b !important;
        }
      `;

      style.style.display = 'none';

      inputWrapper?.appendChild(style);

      initialized.value = true;
    });
  }, 100);
});

const isDigitsFull = () => {
  for (const elem of otpValue.value) {
    if (!elem) {
      return false;
    }
  }

  return true;
};

const handlePaste = (event: ClipboardEvent) => {
  event.preventDefault();
  const pastedText = event.clipboardData?.getData('text') || '';
  const numbers = pastedText
    .replace(/[^0-9]/g, '')
    .slice(0, 6)
    .split('');

  otpValue.value = [...numbers, ...Array(6 - numbers.length).fill('')];

  const inputList = [...otpCont.value!.children] as HTMLInputElement[];
  const nextEmptyIndex = otpValue.value.findIndex(val => !val);
  const focusIndex = nextEmptyIndex === -1 ? 5 : nextEmptyIndex;
  inputList[focusIndex].focus();
  if (isDigitsFull()) {
    validateAndSubmitOtp();
  }
};

const validateAndSubmitOtp = async () => {
  const inputList = [...otpCont.value!.children] as HTMLInputElement[];

  const invalidCodeHandler = () => {
    showToast(t('sign_in.invalid_otp'), { type: 'error' });
    otpValue.value = ['', '', '', '', '', ''];
    inputList[0].focus();
  };

  isSubmitting.value = true;
  await submitOtpMutation(otpValue.value.join(''))
    .then(({ data }) => {
      if (data.challengeName) {
        invalidCodeHandler();
        return;
      }

      return auth.setAsLoggedIn().then(() => {
        router
          .push(!auth.isEmergencyContact() ? '/devices' : '/emergency-contacts')
          .then(() => {
            window.scrollTo(0, 0);
          });
      });
    })
    .catch(() => {
      invalidCodeHandler();
    })
    .finally(() => {
      isSubmitting.value = false;
      document.body.focus();
    });
};

const handleKeyDown = async (event: KeyboardEvent, index: number) => {
  if (
    event.key !== 'Tab' &&
    event.key !== 'ArrowRight' &&
    event.key !== 'ArrowLeft'
  ) {
    event.preventDefault();
  }

  const inputList = [...otpCont.value!.children] as HTMLInputElement[];

  const target = event.target as HTMLInputElement;

  if (target.value.length === 6) {
    otpValue.value = target.value.split('');
  }

  if (event.key === 'Backspace') {
    otpValue.value[index] = '';

    if (index !== 0) {
      inputList[index - 1].focus();
    }

    return;
  }

  if (new RegExp('^([0-9])$').test(event.key)) {
    otpValue.value[index] = event.key;

    if (index !== otpValue.value.length - 1) {
      inputList[index + 1].focus();
    }
  }

  if (isDigitsFull()) {
    validateAndSubmitOtp();
  }
};

watch(
  () => props.otpSent,
  () => {
    if (!props.otpSent) {
      return;
    }

    const inputList = [...otpCont.value!.children] as HTMLInputElement[];

    setTimeout(() => {
      inputList[0].focus();
    }, 100);
  }
);
</script>
<style scoped>
.input-wrapper {
  width: 100%;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(53px, 1fr));
  gap: var(--md-spacing-8);
  justify-items: center;

  &.hidden {
    visibility: hidden;
  }
}

.label {
  display: block;
  text-align: center;
}
</style>
